import React from 'react';
import Alert from 'react-bootstrap/Alert';
import { strings } from '../../../resources/strings';
import { CustomerRecipe } from '../../../models/CustomerRecipe';
import { PairFrameLens } from '../../../models/PairFrameLens';
import { ProductRecipeLinkingRequest } from '../../../models/ProductRecipeLinkingRequest';
import { ProductRecipeLinkingRequestItem } from '../../../models/ProductRecipeLinkingRequestItem';
import ServicesContext from '../../../contexts/ServicesContext';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { Field, FieldType } from '../../../common-components/Field/Field';

interface IMakeLinkingProps {
  orderId?: number;
  customerId?: number;
  linkingRequests: ProductRecipeLinkingRequest[];
  setLinkingRequests: (value: ProductRecipeLinkingRequest[]) => void;
}

const MakeLinking: React.FC<IMakeLinkingProps> = ({
  orderId,
  customerId,
  linkingRequests,
  setLinkingRequests
}) => {

  const [pairsFrameLens, setPairsFrameLens] = React.useState<PairFrameLens[]>([]);
  const [customerRecipes, setCustomerRecipes] = React.useState<CustomerRecipe[]>([]);
  const [isLoadingPairsFrameLens, setIsLoadingPairsFrameLens] = React.useState<boolean>(true);
  const [isLoadingCustomerRecipes, setIsLoadingCustomerRecipes] = React.useState<boolean>(true);

  const {
    getPairsFrameLensByOrderService,
    getCustomerRecipeHistoryByIdService,
    toastService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getPairsFrameLens();
    getCustomerRecipes();
  }, []);

  const customerRecipesOptions = React.useMemo(() => {
    if (isLoadingCustomerRecipes || isLoadingPairsFrameLens) return null;
    const options = [
      { id: 0, name: strings.None }
    ];
    if (customerRecipes && customerRecipes.length > 0) {
      customerRecipes.forEach(recipe => {
        options.push({
          id: recipe.id,
          name: recipe.name && recipe.description
            ? `${recipe.description} (${recipe.name}) - ${FormatHelper.formatDateTime(recipe.createdAt)}`
            : recipe.name || recipe.description
              ? `${recipe.description || ''} ${recipe.name || ''} - ${FormatHelper.formatDateTime(recipe.createdAt)}`
              : FormatHelper.formatDateTime(recipe.createdAt)
        });
      });
    }
    return options;
  }, [customerRecipes, isLoadingCustomerRecipes, isLoadingPairsFrameLens]);

  const framesLenses = React.useMemo(() => {
    if (
      !pairsFrameLens ||
      isLoadingCustomerRecipes || isLoadingPairsFrameLens ||
      !customerRecipesOptions
    ) return null;

    if (pairsFrameLens.length === 0) return (
      <Alert variant='warning'>
        {strings.TheOrderDoesNotHaveFrameLensPairsToLink}
        <div style={{ marginTop: '10px' }}>
          Em "Detalhes do Pedido" e "Grupos de Produtos" é possível definir manualmente os pares de "Armação-Lente".
          Realize o agrupamento manual, depois volte aqui e tente realizar a vinculação novamente.
        </div>
      </Alert>
    );

    return pairsFrameLens.map((pairFrameLens, index) => {
      const links = linkingRequests?.filter(lr =>
        lr.pairNumber === pairFrameLens.pairNumber &&
        lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId))) || [];
      const fieldValue = links.length === 1 ? links[0].customerRecipeId : 0;
      return (
        <Field
          key={`field-frame-lens-${index}`}
          type={FieldType.Select}
          label={pairFrameLens.name}
          selectProps={{
            options: [...customerRecipesOptions],
            displayField: 'name',
            keyField: 'id'
          }}
          value={fieldValue}
          textMuted={links.length > 1 ? 'Itens/produtos vinculados a mais de uma receita. Resolva a vinculação duplicada deste item/produto.' : ''}
          change={(value) => changeSelectedCustomerRecipe(value, pairFrameLens)}
        />
      );
    });
  }, [pairsFrameLens, customerRecipesOptions, linkingRequests, isLoadingCustomerRecipes, isLoadingPairsFrameLens]);

  return (
    <>
      {framesLenses}
    </>
  );

  function getPairsFrameLens() {
    if (!orderId) return;
    setIsLoadingPairsFrameLens(true);
    getPairsFrameLensByOrderService.handle({ orderId: orderId, orderToken: '', considerFlatLenses: true })
      .then(result => {
        setPairsFrameLens(result?.value?.pairs || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setIsLoadingPairsFrameLens(false);
      });
  }

  function getCustomerRecipes() {
    if (!customerId) return;
    setIsLoadingCustomerRecipes(true);
    getCustomerRecipeHistoryByIdService.handle({ customerId: customerId })
      .then(result => {
        setCustomerRecipes(result?.value?.recipes || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setIsLoadingCustomerRecipes(false);
      });
  }

  function changeSelectedCustomerRecipe(customerRecipeId: number, pairFrameLens: PairFrameLens) {
    customerRecipeId = +customerRecipeId || 0;
    const filteredLinkingRequests = getLinkingRequestsByPairFrameLens(pairFrameLens);
    if (filteredLinkingRequests.length === 0 && customerRecipeId > 0) {
      setLinkingRequests([
        ...linkingRequests,
        createLinkingRequest(customerRecipeId, pairFrameLens)
      ]);
    } else if (filteredLinkingRequests.length > 0 && customerRecipeId === 0) {
      setLinkingRequests(removeLinkingRequestsByPairFrameLens(pairFrameLens));
    } else if (filteredLinkingRequests.length > 0 && customerRecipeId > 0) {
      setLinkingRequests([
        ...removeLinkingRequestsByPairFrameLens(pairFrameLens),
        createLinkingRequest(customerRecipeId, pairFrameLens)
      ]);
    }
  }

  function getLinkingRequestsByPairFrameLens(pairFrameLens: PairFrameLens) {
    return linkingRequests.filter(lr =>
      lr.pairNumber === pairFrameLens.pairNumber &&
      lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId)));
  }

  function removeLinkingRequestsByPairFrameLens(pairFrameLens: PairFrameLens) {
    return linkingRequests.filter(lr =>
      lr.pairNumber !== pairFrameLens.pairNumber ||
      !lr.items.every(lri => pairFrameLens.items.some(pfli => pfli.id === lri.orderItemId)));
  }

  function createLinkingRequest(customerRecipeId: number, pairFrameLens: PairFrameLens) {
    const linkingRequest = new ProductRecipeLinkingRequest();
    linkingRequest.customerRecipeId = customerRecipeId;
    linkingRequest.frameSku = pairFrameLens.frameSku;
    linkingRequest.lensSku = pairFrameLens.lensSku;
    linkingRequest.pairNumber = pairFrameLens.pairNumber;
    linkingRequest.items = pairFrameLens.items.map(pfli => {
      const item = new ProductRecipeLinkingRequestItem();
      item.orderItemId = pfli.id;
      return item;
    });
    return linkingRequest;
  }
}

export { MakeLinking };